import useFormattedDate from "@/dates/use-formatted-date";
import {
  ArticleChildren,
  ArticleImagesStack,
  ArticlesGroup,
  ArticlesGroupAction,
  ArticlesGroupContent,
  ArticlesGroupFooter,
  ArticlesGroupSubtitle,
  ArticlesGroupTitle,
} from "@babackapp/customer-ui/components/article";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import type { SearchReturnRequestsResponseDto } from "@babackapp/private-api-client";
import { ChevronDown } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import { useLineItemImages } from "../../hooks/use-line-item-images";
import { ReturnItemsSummary } from "./return-items-summary";

type ReturnRequest = SearchReturnRequestsResponseDto["returnRequests"][number];

type Props = {
  returnRequest: ReturnRequest;
};

export function ReturnContent({ returnRequest }: Props) {
  const [expanded, setExpanded] = useState(false);
  const lineItems = useLineItemImages(returnRequest);
  const createdAt = useFormattedDate(returnRequest.createdAt);
  const t = useCustomText();

  return (
    <ArticlesGroup
      layout={false}
      variants={{}}
      className="cursor-pointer"
      onClick={() => setExpanded((e) => !e)}
    >
      <ArticlesGroupContent
        layout={false}
        disableVariants={{ enter: true, exit: true, visible: true }}
        className={expanded ? "border-b-transparent pt-0" : undefined}
      >
        <ArticlesGroupTitle layoutId={returnRequest.id + "-title"}>
          {t("returnRequests.item.title", { date: createdAt.formattedDate })}
        </ArticlesGroupTitle>
        <ArticlesGroupSubtitle layoutId={returnRequest.id + "-subtitle"} className="max-w-[340px]">
          {t("returnRequests.item.subtitle", { count: lineItems.length })}
        </ArticlesGroupSubtitle>
        <ArticlesGroupFooter
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.3, ease: [0.4, 0, 0.2, 1] } }}
          exit={{ opacity: 0, x: 10 }}
        />

        {!expanded && (
          <ArticleImagesStack
            grid
            layoutIdPrefix={returnRequest.id}
            srcs={lineItems.map((li) => li.image).filter((src): src is string => !!src)}
            ids={lineItems.map((li) => li.line_item_id)}
          />
        )}

        <ArticlesGroupAction layoutId="action">
          <motion.span
            layoutId="action-rotate"
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown />
          </motion.span>
        </ArticlesGroupAction>

        {expanded && (
          <ArticleChildren>
            <ReturnItemsSummary returnRequest={returnRequest} />
          </ArticleChildren>
        )}
      </ArticlesGroupContent>
    </ArticlesGroup>
  );
}
